import { Roles } from "enum/roles.enum";
import { IUser } from "models/user/user.model";
import { roleGuard } from "utils/roleGuard";
import { isDemoMode } from "utils/isDemoMode";

/**
 * Portal capabilities tied to normal customer login. `itd-customer-user-admin`
 * inherits these; extras (e.g. user CRUD + reset flows) stay behind
 * {@link PermissionCheck.users}.canManageActions.
 */
const CUSTOMER_SELF_SERVICE_ROLES: Roles[] = [
  Roles.ITD_CUSTOMER,
  Roles.ITD_CUSTOMER_USER_ADMIN,
];

export class PermissionCheck {
  user: IUser | null;

  constructor({ user }: { user: IUser | null }) {
    this.user = user;
  }

  addresses = {
    canCreate: (): boolean => {
      return true;
    },

    canEdit: (): boolean => {
      return true;
    },

    canDelete: (): boolean => {
      return true;
    },
  };

  bulkShipments = {
    canCreate: (): boolean => {
      return (
        (this.user &&
          roleGuard(this.user["https://connexx.com/roles"], [
            ...CUSTOMER_SELF_SERVICE_ROLES,
            Roles.ITD_ADMIN_FOR_SALES_ACCOUNTS,
            Roles.ITD_CUSTOMER_WITH_ACCESS_TO_CHILD_ACCOUNTS,
            Roles.ITD_SALES_PEOPLE_FOR_SALES_ACCOUNTS,
          ])) ||
        false
      );
    },

    canView: (): boolean => {
      return (
        (this.user &&
          roleGuard(this.user["https://connexx.com/roles"], [
            ...CUSTOMER_SELF_SERVICE_ROLES,
            Roles.ITD_ADMIN_FOR_SALES_ACCOUNTS,
            Roles.ITD_CUSTOMER_WITH_ACCESS_TO_CHILD_ACCOUNTS,
            Roles.ITD_SALES_PEOPLE_FOR_SALES_ACCOUNTS,
          ])) ||
        false
      );
    },
  };

  childAccounts = {
    // The server also limits this to the Walker Logistics account trees.
    canAdd: (): boolean => {
      return (
        (this.user &&
          roleGuard(this.user["https://connexx.com/roles"], [
            Roles.ITD_STAFF,
            Roles.ITD_ADMIN_FOR_SALES_ACCOUNTS,
            Roles.ITD_CUSTOMER_WITH_ACCESS_TO_CHILD_ACCOUNTS,
          ])) ||
        false
      );
    },
  };

  pickup = {
    canCreate: (): boolean => {
      return (
        (this.user &&
          roleGuard(this.user["https://connexx.com/roles"], [
            ...CUSTOMER_SELF_SERVICE_ROLES,
            Roles.ITD_ADMIN_FOR_SALES_ACCOUNTS,
            Roles.ITD_CUSTOMER_WITH_ACCESS_TO_CHILD_ACCOUNTS,
            Roles.ITD_SALES_PEOPLE_FOR_SALES_ACCOUNTS,
          ])) ||
        false
      );
    },

    canView: (): boolean => {
      return (
        (this.user &&
          roleGuard(this.user["https://connexx.com/roles"], [
            ...CUSTOMER_SELF_SERVICE_ROLES,
            Roles.ITD_ADMIN_FOR_SALES_ACCOUNTS,
            Roles.ITD_CUSTOMER_WITH_ACCESS_TO_CHILD_ACCOUNTS,
            Roles.ITD_SALES_PEOPLE_FOR_SALES_ACCOUNTS,
          ])) ||
        false
      );
    },
  };

  bulkShipmentProfiles = {
    canCreate: (): boolean => {
      return true;
    },

    canEdit: (): boolean => {
      return true;
    },

    canDelete: (): boolean => {
      return true;
    },
  };

  labelJobs = {
    canView: (): boolean => {
      return (
        (this.user &&
          roleGuard(this.user["https://connexx.com/roles"], [
            ...CUSTOMER_SELF_SERVICE_ROLES,
            Roles.ITD_ADMIN_FOR_SALES_ACCOUNTS,
            Roles.ITD_CUSTOMER_WITH_ACCESS_TO_CHILD_ACCOUNTS,
            Roles.ITD_SALES_PEOPLE_FOR_SALES_ACCOUNTS,
          ])) ||
        false
      );
    },
  };

  quotes = {
    canCreate: (): boolean => {
      return true;
    },

    canEmailQuote: (): boolean => {
      return (
        (this.user &&
          roleGuard(this.user["https://connexx.com/roles"], [
            Roles.ITD_STAFF,
          ])) ||
        false
      );
    },

    canConvertQuoteToShipment: (): boolean => {
      return (
        (this.user &&
          roleGuard(this.user["https://connexx.com/roles"], [
            ...CUSTOMER_SELF_SERVICE_ROLES,
            Roles.ITD_ADMIN_FOR_SALES_ACCOUNTS,
          ])) ||
        false
      );
    },
  };

  rules = {
    canUseAiSuggestions: (): boolean => {
      return (
        (this.user &&
          roleGuard(this.user["https://connexx.com/roles"], [
            Roles.ITD_ADMIN_FOR_SALES_ACCOUNTS,
          ])) ||
        false
      );
    },

    canCreate: (): boolean => {
      return true;
    },

    canEdit: (): boolean => {
      return true;
    },

    canDelete: (): boolean => {
      return true;
    },

    canSaveRule: (): boolean => {
      return (
        (this.user &&
          roleGuard(this.user["https://connexx.com/roles"], [
            ...CUSTOMER_SELF_SERVICE_ROLES,
            Roles.ITD_ADMIN_FOR_SALES_ACCOUNTS,
            Roles.ITD_CUSTOMER_WITH_ACCESS_TO_CHILD_ACCOUNTS,
            Roles.ITD_STAFF,
          ])) ||
        false
      );
    },
  };

  shipments = {
    canGenerate: (): boolean => {
      // this is the actual function to generate a real label.
      return (
        (this.user &&
          roleGuard(this.user["https://connexx.com/roles"], [
            ...CUSTOMER_SELF_SERVICE_ROLES,
            Roles.ITD_ADMIN_FOR_SALES_ACCOUNTS,
            Roles.ITD_CUSTOMER_WITH_ACCESS_TO_CHILD_ACCOUNTS,
            Roles.ITD_SALES_PEOPLE_FOR_SALES_ACCOUNTS,
          ])) ||
        false
      );
    },

    canDelete: (): boolean => {
      // this is the actual function to generate a real label.
      return (
        (this.user &&
          roleGuard(this.user["https://connexx.com/roles"], [
            ...CUSTOMER_SELF_SERVICE_ROLES,
            Roles.ITD_ADMIN_FOR_SALES_ACCOUNTS,
            Roles.ITD_CUSTOMER_WITH_ACCESS_TO_CHILD_ACCOUNTS,
          ])) ||
        false
      );
    },

    canCreate: (): boolean => {
      // this is the function to show the create shipment button in the header
      return (
        (this.user &&
          roleGuard(this.user["https://connexx.com/roles"], [
            ...CUSTOMER_SELF_SERVICE_ROLES,
            Roles.ITD_ADMIN_FOR_SALES_ACCOUNTS,
            Roles.ITD_SALES_PEOPLE_FOR_SALES_ACCOUNTS,
            Roles.ITD_CUSTOMER_WITH_ACCESS_TO_CHILD_ACCOUNTS,
          ])) ||
        false
      );
    },

    canView: (): boolean => {
      return (
        (this.user &&
          roleGuard(this.user["https://connexx.com/roles"], [
            ...CUSTOMER_SELF_SERVICE_ROLES,
            Roles.ITD_ADMIN_FOR_SALES_ACCOUNTS,
            Roles.ITD_SALES_PEOPLE_FOR_SALES_ACCOUNTS,
            Roles.ITD_CUSTOMER_WITH_ACCESS_TO_CHILD_ACCOUNTS,
          ])) ||
        false
      );
    },

    // Internal-only "Shipment Status" table column (and its Status filter);
    // customers see the same chip in the shipment drawer instead. The demo
    // site shows them to everyone, prospects included.
    canViewShipmentStatusColumn: (): boolean => {
      return (
        isDemoMode() ||
        (this.user &&
          roleGuard(this.user["https://connexx.com/roles"], [
            Roles.ITD_STAFF,
            Roles.ITD_ADMIN_FOR_SALES_ACCOUNTS,
            Roles.ITD_SALES_PEOPLE_FOR_SALES_ACCOUNTS,
          ])) ||
        false
      );
    },
  };

  settings = {
    canEdit: (): boolean => {
      return (
        (this.user &&
          roleGuard(this.user["https://connexx.com/roles"], [
            ...CUSTOMER_SELF_SERVICE_ROLES,
            Roles.ITD_ADMIN_FOR_SALES_ACCOUNTS,
            Roles.ITD_SALES_PEOPLE_FOR_SALES_ACCOUNTS,
            Roles.ITD_CUSTOMER_WITH_ACCESS_TO_CHILD_ACCOUNTS,
          ])) ||
        false
      );
    },
  };

  salesAccounts = {
    canChange: (): boolean => {
      return (
        (this.user &&
          roleGuard(this.user["https://connexx.com/roles"], [
            Roles.ITD_ADMIN_FOR_SALES_ACCOUNTS,
            Roles.ITD_SALES_PEOPLE_FOR_SALES_ACCOUNTS,
            Roles.ITD_CUSTOMER_WITH_ACCESS_TO_CHILD_ACCOUNTS,
          ])) ||
        false
      );
    },
  };

  demoAccounts = {
    // "Create demo account" in the header, for ITD staff, sales-account
    // admins and salespeople. The accounts always live on the demo site: on
    // staging and live the server passes the requests on to it.
    canCreate: (): boolean => {
      return (
        (this.user &&
          roleGuard(this.user["https://connexx.com/roles"], [
            Roles.ITD_STAFF,
            Roles.ITD_ADMIN_FOR_SALES_ACCOUNTS,
            Roles.ITD_SALES_PEOPLE_FOR_SALES_ACCOUNTS,
          ])) ||
        false
      );
    },
  };

  notifications = {
    // Authoring/sending customer notifications: ITD staff + sales-account admins.
    canManage: (): boolean => {
      return (
        (this.user &&
          roleGuard(this.user["https://connexx.com/roles"], [
            Roles.ITD_STAFF,
            Roles.ITD_ADMIN_FOR_SALES_ACCOUNTS,
          ])) ||
        false
      );
    },
  };

  users = {
    canView: (): boolean => {
      return (
        (this.user &&
          roleGuard(this.user["https://connexx.com/roles"], [
            ...CUSTOMER_SELF_SERVICE_ROLES,
            Roles.ITD_CUSTOMER_WITH_ACCESS_TO_CHILD_ACCOUNTS,
            Roles.ITD_ADMIN_FOR_SALES_ACCOUNTS,
          ])) ||
        false
      );
    },
    canManageActions: (): boolean => {
      return (
        (this.user &&
          roleGuard(this.user["https://connexx.com/roles"], [
            Roles.ITD_ADMIN_FOR_SALES_ACCOUNTS,
            Roles.ITD_CUSTOMER_USER_ADMIN,
          ])) ||
        false
      );
    },
  };
}
